import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Typography,
  showErrorAlert,
  Spinner,
  Dialog,
  DIALOG_TYPES,
} from 'components/alf-design';
import { withApi, usePolicy, useAuth0 } from 'utils';

import PolicyContactCard from './PolicyContactCard';
import MyPoliciesCard from './MyPoliciesCard';
import AccountSecurityCard from './AccountSecurityCard';
import LinkPolicy from './LinkPolicy';
import ResetPasswordModal from './ResetPasswordModal';
import UpdateUsername from './UpdateUsername';
import './UserProfile.css';

const getErrorDescription = (err, request) => {
  if (err?.response) return err.response.data.error;
  if (err?.request)
    return 'Network error. Please check your connection and try again.';
  return `Error attempting to fetch your ${request}. Refresh page to try again.`;
};

const UserProfile = ({ api, history }) => {
  const { user, logout } = useAuth0();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [showLinkPolicy, setShowLinkPolicy] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showUpdateUsername, setShowUpdateUsername] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const isAdmin = !!(
    user?.email?.includes('@slideinsurance.com') && user?.role?.isAdmin
  );
  const {
    error,
    policies,
    loading,
    setLoading,
    selectedPolicy,
    selectedPolicyFusionNH,
    fetchPolicy,
  } = usePolicy({
    loadPolicies: !isAdmin,
  });

  const policyFromUrl = searchParams.get('policy');

  useEffect(() => {
    document.title = 'My Profile | Slide Insurance';
  }, []);

  useEffect(() => {
    if (policies || error) setLoading(false);
    if (error) {
      showErrorAlert({
        message: `${error.entity?.charAt(0).toUpperCase() + error.entity?.slice(1)} Error`,
        description: getErrorDescription(error.err, error.entity),
      });
    }
  }, [policies, error, setLoading]);

  useEffect(() => {
    if (isAdmin || !policies?.length) return;
    const urlPolicyIsValid =
      policyFromUrl && policies.some((p) => p.policyNumber === policyFromUrl);
    if (urlPolicyIsValid) return;
    const fallbackPolicy =
      selectedPolicy?.policyNumber || policies[0].policyNumber;
    navigate(`/user-profile?policy=${encodeURIComponent(fallbackPolicy)}`, {
      replace: true,
    });
  }, [isAdmin, policies, policyFromUrl, selectedPolicy, navigate]);

  const handleLogout = () => {
    window.history.replaceState(null, null, '/');
    logout({ returnTo: window.location.origin });
  };

  const isInitialLoading = !isAdmin && loading && policies == null;
  const showPolicySections = !isAdmin && policies && policies.length > 0;

  if (isInitialLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner id="dashboard-spinner" size={80} />
      </div>
    );
  }

  return (
    <div className="mt-0">
      <Typography
        id="profile-page-title"
        component="h1"
        type="h1"
        className="mb-[24px]"
      >
        My Profile
      </Typography>

      <div
        className={`grid gap-[24px] ${showPolicySections ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1'}`}
      >
        {showPolicySections && (
          <PolicyContactCard
            api={api}
            policies={policies}
            selectedPolicy={selectedPolicy}
            selectedPolicyFusionNH={selectedPolicyFusionNH}
            loading={loading}
            fetchPolicy={fetchPolicy}
          />
        )}

        <div
          className={`min-w-0 flex flex-col gap-[24px] ${!showPolicySections ? 'lg:max-w-[480px] lg:mx-auto lg:w-full' : ''}`}
        >
          {showPolicySections && (
            <MyPoliciesCard
              policies={policies}
              isAdmin={isAdmin}
              onLinkPolicy={() => setShowLinkPolicy(true)}
            />
          )}
          <AccountSecurityCard
            userEmail={user?.email}
            isAdmin={isAdmin}
            onUpdateUsername={() => setShowUpdateUsername(true)}
            onResetPassword={() => setShowResetPassword(true)}
            onLogOut={() => setShowLogoutConfirm(true)}
          />
        </div>
      </div>

      <LinkPolicy
        show={showLinkPolicy}
        onClose={() => setShowLinkPolicy(false)}
        api={api}
        history={history}
      />
      <ResetPasswordModal
        show={showResetPassword}
        onClose={() => setShowResetPassword(false)}
        currentEmail={user?.email}
        api={api}
      />
      <UpdateUsername
        show={showUpdateUsername}
        onClose={() => setShowUpdateUsername(false)}
      />

      <Dialog
        id="logout-confirm"
        type={DIALOG_TYPES.SIMPLE}
        show={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        headerText="Are you sure?"
        primaryButtonText="Log out"
        secondaryButtonText="Close"
        onPrimaryClick={handleLogout}
        onSecondaryClick={() => setShowLogoutConfirm(false)}
        ariaLabel="Log Out"
        wrapperStyle={{ width: '370px' }}
      />
    </div>
  );
};

UserProfile.propTypes = {
  api: PropTypes.object,
  history: PropTypes.object,
};

export default withApi(UserProfile);
